import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_OPTIONS = ["Running", "Idle", "Setup", "Maintenance", "Breakdown", "Power Off"];

// Build a wss:// URL for the machines feed from the same origin as REACT_APP_BACKEND_URL.
function _wsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  try {
    const u = new URL(base);
    const scheme = u.protocol === "https:" ? "wss:" : "ws:";
    return `${scheme}//${u.host}/api/ws/machines`;
  } catch {
    return "";
  }
}

export default function LiveProduction() {
  const [machines, setMachines] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState("Running");
  // Sep 03, 2026 · Drill-down from Work Orders — `?machine=<code>`
  // scrolls the board to the requested loom, opens its status card,
  // and paints a temporary ring so operators land on the exact row
  // that was clicked from the WO grid.
  const [searchParams] = useSearchParams();
  const focusCode = (searchParams.get("machine") || "").trim();
  const cardRefs = useRef({});
  const [focusedCode, setFocusedCode] = useState("");
  // Feb 2026 · WebSocket live stream with graceful HTTP polling fallback.
  //  live  = last update source ("ws" | "http" | "off")
  //  wsRef = active socket for cleanup
  const [live, setLive] = useState("off");
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const fetchOnce = async () => {
    try {
      const { data } = await api.get("/machines/");
      setMachines(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    let cancelled = false;
    // Prime the board immediately so the user isn't staring at nothing while
    // the socket handshake completes.
    fetchOnce().then(() => { if (!cancelled) setLive("http"); });

    const url = _wsUrl();
    if (!url) return () => { cancelled = true; };

    let ws;
    try {
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        if (cancelled) return;
        setLive("ws");
        // Kill any polling fallback the moment the socket is up.
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      };
      ws.onmessage = (evt) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(evt.data);
          if (msg?.type === "machines" && Array.isArray(msg.machines)) {
            setMachines(msg.machines);
            setLive("ws");
          }
        } catch { /* ignore malformed frames */ }
      };
      ws.onerror = () => {
        // Silent — the close handler flips to polling.
      };
      ws.onclose = () => {
        if (cancelled) return;
        setLive("http");
        if (!pollRef.current) {
          pollRef.current = setInterval(fetchOnce, 5000);
        }
      };
    } catch (e) {
      pollRef.current = setInterval(fetchOnce, 5000);
      setLive("http");
    }

    return () => {
      cancelled = true;
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } wsRef.current = null; }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, []);

  const filtered = filter === "all" ? machines : machines.filter(m => m.status === filter);

  const stats = {
    total: machines.length,
    Running: machines.filter(m => m.status === "Running").length,
    Idle: machines.filter(m => m.status === "Idle" || m.status === "Setup").length,
    Breakdown: machines.filter(m => m.status === "Breakdown").length,
    Maintenance: machines.filter(m => m.status === "Maintenance").length,
    "Power Off": machines.filter(m => m.status === "Power Off").length,
  };

  const updateStatus = async () => {
    try {
      await api.post(`/machines/${selected.id}/status`, { status: newStatus });
      toast.success(`${selected.code} → ${newStatus}`);
      setSelected(null);
      fetchOnce();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const liveLabel = live === "ws" ? "LIVE · WS" : live === "http" ? "LIVE · POLL" : "OFFLINE";
  const liveCls = live === "ws" ? "text-emerald-400" : live === "http" ? "text-amber-400" : "text-zinc-500";
  const liveDot = live === "ws" ? "bg-emerald-400 animate-pulse" : live === "http" ? "bg-amber-400" : "bg-zinc-500";

  // Sep 03, 2026 · Once machines finish loading, scroll & highlight the
  // deep-linked loom then open its status modal so the drill-down feels
  // like a one-click navigation from the WO grid.
  useEffect(() => {
    if (!focusCode || machines.length === 0) return;
    const target = machines.find(m => m.code === focusCode);
    if (!target) return;
    setFocusedCode(focusCode);
    const el = cardRefs.current[focusCode];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setSelected(target);
    setNewStatus(target.status);
    const t = setTimeout(() => setFocusedCode(""), 2500);
    return () => clearTimeout(t);
    // Only run when machines first arrive AND when the query param
    // changes; intentionally omitting scroll-side effects to avoid
    // re-scrolling on every 30s data refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCode, machines.length]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            Real-time shop floor
            <span data-testid="live-indicator" className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest ${liveCls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${liveDot}`} />
              {liveLabel}
            </span>
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Live Production Board</h1>
        </div>
      </div>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: `All (${stats.total})`, color: "bg-white/5 text-white" },
          { key: "Running", label: `Running (${stats.Running})`, color: "bg-emerald-500/10 text-emerald-400" },
          { key: "Idle", label: `Idle (${stats.Idle})`, color: "bg-amber-500/10 text-amber-400" },
          { key: "Breakdown", label: `Breakdown (${stats.Breakdown})`, color: "bg-red-500/10 text-red-400" },
          { key: "Maintenance", label: `Maintenance (${stats.Maintenance})`, color: "bg-blue-500/10 text-blue-400" },
          { key: "Power Off", label: `Off (${stats["Power Off"]})`, color: "bg-zinc-500/10 text-zinc-400" },
        ].map(f => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium border ${
              filter === f.key ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white" : `border-[var(--border)] ${f.color} hover:border-zinc-600`
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Machine grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {filtered.map((m, i) => {
          const color =
            m.status === "Running" ? "border-emerald-500/40" :
            m.status === "Breakdown" ? "border-red-500/40" :
            m.status === "Maintenance" ? "border-blue-500/40" :
            m.status === "Power Off" ? "border-zinc-600" :
            "border-amber-500/40";
          return (
            <button
              key={m.id}
              data-testid={`machine-card-${m.code}`}
              ref={el => { cardRefs.current[m.code] = el; }}
              onClick={() => { setSelected(m); setNewStatus(m.status); }}
              className={`machine-card text-left tactical-card p-3 border-l-2 ${color} animate-in ${focusedCode === m.code ? "ring-2 ring-[var(--accent)]" : ""}`}
              style={{ animationDelay: `${i * 15}ms` }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-sm font-semibold text-white">{m.code}</div>
                <StatusBadge status={m.status} pulse={m.status === "Running" || m.status === "Breakdown"} />
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest truncate mb-3">{m.machine_type}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase">RPM</div>
                  <div data-testid={`machine-rpm-${m.code}`} className="font-mono text-white">{m.current_rpm || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase">Eff</div>
                  <div data-testid={`machine-eff-${m.code}`} className={`font-mono ${m.current_efficiency >= 85 ? "text-emerald-400" : m.current_efficiency >= 70 ? "text-amber-400" : "text-zinc-400"}`}>
                    {m.current_efficiency || 0}%
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] text-zinc-500 uppercase">Today</div>
                  <div className="font-mono text-white">{m.today_production || 0}<span className="text-zinc-500 text-[10px] ml-1">m</span></div>
                </div>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full py-16 text-center text-zinc-500">No machines match this filter</div>
        )}
      </div>

      {/* Machine detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white" data-testid="machine-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              {selected?.code}
              {selected && <StatusBadge status={selected.status} />}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-[var(--bg-2)] rounded-sm">
                  <div className="overline mb-1">Machine Type</div>
                  <div className="text-white">{selected.machine_type}</div>
                </div>
                <div className="p-3 bg-[var(--bg-2)] rounded-sm">
                  <div className="overline mb-1">Department</div>
                  <div className="text-white">{selected.department || "—"}</div>
                </div>
                <div className="p-3 bg-[var(--bg-2)] rounded-sm">
                  <div className="overline mb-1">Capacity</div>
                  <div className="font-mono text-white">{selected.capacity_mtr_hr} m/hr</div>
                </div>
                <div className="p-3 bg-[var(--bg-2)] rounded-sm">
                  <div className="overline mb-1">Std RPM</div>
                  <div className="font-mono text-white">{selected.standard_rpm}</div>
                </div>
                <div className="p-3 bg-[var(--bg-2)] rounded-sm">
                  <div className="overline mb-1">Current RPM</div>
                  <div className="font-mono text-white">{selected.current_rpm}</div>
                </div>
                <div className="p-3 bg-[var(--bg-2)] rounded-sm">
                  <div className="overline mb-1">Efficiency</div>
                  <div className="font-mono text-emerald-400">{selected.current_efficiency}%</div>
                </div>
              </div>

              <div className="p-3 bg-[var(--bg-2)] rounded-sm">
                <div className="overline mb-2">Change Status</div>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="machine-status-select" className="bg-[var(--bg-0)] border-[var(--border)] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                    {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setSelected(null)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400 hover:text-white">Cancel</button>
            <button data-testid="machine-status-save" onClick={updateStatus} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">Update</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
